import React from 'react';
import { format } from 'date-fns';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Separator } from '@/components/ui/Separator';
import { User, AlertCircle, Award, CalendarRange, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { ScheduleOccurrence } from '../../hooks/useSchedule';
import { getOccurrenceScopeLabel } from '../../utils/bookingScopeLabel';
import { isEligibleForMakeupCredit } from '@court-sync/shared';

interface LessonDetailModalProps {
  occurrence: ScheduleOccurrence | null;
  isOpen: boolean;
  onClose: () => void;
  onReportAbsence?: (occurrence: ScheduleOccurrence) => void;
  onRequestRefund?: (occurrence: ScheduleOccurrence) => void;
  studentName?: string;
  creditsPath?: string;
  reportSuccess?: boolean;
  /** Base lessons path for rebook CTA (e.g. /academy/:slug/lessons) */
  lessonsPath?: string;
}

export function LessonDetailModal({
  occurrence,
  isOpen,
  onClose,
  onReportAbsence,
  onRequestRefund,
  studentName,
  creditsPath = '/credits',
  reportSuccess,
  lessonsPath,
}: LessonDetailModalProps) {
  if (!occurrence) return null;

  const now = new Date();
  const lessonStart = new Date(occurrence.date);
  const [hours, minutes] = occurrence.startTime.split(':').map(Number);
  lessonStart.setHours(hours, minutes, 0, 0);

  const isPast = lessonStart < now;
  const canReportAbsence =
    !isPast &&
    occurrence.makeupLessonEnabled &&
    occurrence.status === 'scheduled' &&
    isEligibleForMakeupCredit(
      now,
      lessonStart,
      occurrence.makeupNotificationDeadlineHours
    );

  const getStatusBadge = () => {
    if (occurrence.status === 'cancelled') {
      if (occurrence.isRefunded) {
        return (
          <Badge variant="secondary" className="text-xs bg-green-50 border-green-200 text-green-800 dark:bg-green-900/30 dark:text-green-200">
            Refunded
          </Badge>
        );
      }
      return (
        <Badge variant="destructive" className="text-xs">
          Cancelled
          {occurrence.override?.reason && `: ${occurrence.override.reason}`}
        </Badge>
      );
    }
    if (occurrence.status === 'postponed') {
      const newDate = occurrence.override?.newDate;
      return (
        <Badge variant="secondary" className="text-xs bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200">
          Postponed
          {newDate && ` → ${format(new Date(newDate), 'MMM d')}`}
        </Badge>
      );
    }
    if (isPast) {
      return (
        <Badge variant="secondary" className="text-xs">
          Past
        </Badge>
      );
    }
    return (
      <Badge variant="default" className="text-xs bg-green-600">
        Scheduled
      </Badge>
    );
  };

  const scopeLabel = getOccurrenceScopeLabel({
    bookingScope: occurrence.bookingScope,
    lessonType: occurrence.lessonType,
  });

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{occurrence.lessonName}</DialogTitle>
          <DialogDescription>
            {format(occurrence.date, 'EEEE, MMMM d')} · {occurrence.startTime} – {occurrence.endTime}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="flex flex-wrap items-center gap-2">
            {getStatusBadge()}
            {scopeLabel && (
              <Badge variant="secondary" className="text-xs bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                {scopeLabel}
              </Badge>
            )}
            <Badge variant="outline" className="text-xs">
              {occurrence.lessonType}
            </Badge>
            {occurrence.skillLevel && (
              <Badge variant="outline" className="text-xs capitalize">
                {occurrence.skillLevel}
              </Badge>
            )}
          </div>

          <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
            <Clock className="h-4 w-4 flex-shrink-0" />
            <span>
              {format(occurrence.date, 'EEEE, MMM d, yyyy')} · {occurrence.startTime} – {occurrence.endTime}
            </span>
          </div>

          {(occurrence.studentName ?? studentName) && (
            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
              <User className="h-4 w-4 flex-shrink-0" />
              <span>Student: {occurrence.studentName ?? studentName}</span>
            </div>
          )}

          {(occurrence.termName || occurrence.termDateRange) && (
            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
              <CalendarRange className="h-4 w-4 flex-shrink-0" />
              <span>
                Term: {occurrence.termName || occurrence.termDateRange}
                {occurrence.termName && occurrence.termDateRange && ` (${occurrence.termDateRange})`}
              </span>
            </div>
          )}

          {occurrence.makeupLessonEnabled && occurrence.status === 'scheduled' && !isPast && (
            <>
              <Separator />
              <div className="text-xs text-gray-600 dark:text-gray-400 space-y-2">
                <p>
                  Report absence at least {occurrence.makeupNotificationDeadlineHours}h before the
                  lesson to earn a make-up credit.
                </p>
                {!canReportAbsence && onReportAbsence && (
                  <div className="flex items-start gap-2 rounded-md bg-amber-50 dark:bg-amber-900/20 p-2 text-amber-800 dark:text-amber-200">
                    <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                    <span>Deadline passed. Report absences at least {occurrence.makeupNotificationDeadlineHours} hours before the lesson.</span>
                  </div>
                )}
              </div>
            </>
          )}

          {reportSuccess && (
            <div className="rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 p-3">
              <div className="flex items-start gap-2">
                <Award className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-green-800 dark:text-green-200">
                    Make-up credit earned!
                  </p>
                  <p className="text-xs text-green-700 dark:text-green-300 mt-1">
                    You can use it to book a make-up lesson in the Make-up credits page.
                  </p>
                  {creditsPath && (
                    <Link
                      to={creditsPath}
                      className="inline-block mt-2 text-sm font-medium text-green-600 dark:text-green-400 hover:underline"
                      onClick={onClose}
                    >
                      Go to Make-up credits →
                    </Link>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          {occurrence.status === 'cancelled' && !occurrence.isRefunded && (
            <div className="flex flex-wrap gap-2 mr-auto">
              {lessonsPath && (
                <Link
                  to={`${lessonsPath}?rebookFrom=${occurrence.bookingId}&lessonType=${occurrence.lessonType}${occurrence.skillLevel ? `&skillLevel=${occurrence.skillLevel}` : ''}`}
                  onClick={onClose}
                >
                  <Button>Pick Another Time</Button>
                </Link>
              )}
              {onRequestRefund && (
                <Button variant="outline" onClick={() => onRequestRefund(occurrence)}>
                  Request Refund
                </Button>
              )}
            </div>
          )}
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          {canReportAbsence && onReportAbsence && !reportSuccess && (
            <Button onClick={() => onReportAbsence(occurrence)}>
              Report Absence
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
